import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { ApiRow, ApiSuccess, ApiSuccessOf } from "@AxiosWrapper";
import type { StickySidebarIcon } from "@Components/StickySidebar/useStickySidebar";

/**
 * Each server-data slice below holds the value its single dispatcher sends, named by the route that
 * dispatcher calls (`ApiRow` / `ApiSuccess` / `ApiSuccessOf` on its path), so `RootState["roleData"]`
 * &c. follow that route's contract.
 *
 * `action.payload ?? state` where the route's `result` is optional: returning the draft is immer's
 * "no change", the same as returning an `undefined` payload.
 */

/** The four fields `useStickySidebar` maps out of each icon before dispatching them. */
type SidebarIconNameEntry = Pick<StickySidebarIcon, "id" | "name" | "svg" | "toolTip">;

// Slice to store the list of roles within a project
// Dispatched by `getProjectRoles` (actions/projectSetting.ts) with the `/projects/getProjectRoles` `result`.
const initialRoleDataState: ApiRow<"/projects/getProjectRoles">[] = [];
const roleDataSlice = createSlice({
  name: "roleData",
  initialState: initialRoleDataState,
  reducers: {
    setProjectRolesList: (state, action: PayloadAction<ApiSuccess<"/projects/getProjectRoles">["result"]>) =>
      action.payload ?? state,
  },
});
export const { setProjectRolesList } = roleDataSlice.actions;
export const roleData = roleDataSlice.reducer;

// Slice to store list of users inside spaces
// Dispatched by `getUsersInSpaces` (design-viewer/DesignViewer.tsx) with the `/spaces/getSpaceUsersList` `result`.
const initialSpacesUsersDataState: ApiRow<"/spaces/getSpaceUsersList">[] = [];
const spacesUsersDataSlice = createSlice({
  name: "spacesUsersData",
  initialState: initialSpacesUsersDataState,
  reducers: {
    setSpacesInProjectList: (_state, action: PayloadAction<ApiSuccess<"/spaces/getSpaceUsersList">["result"]>) =>
      action.payload,
  },
});
export const { setSpacesInProjectList } = spacesUsersDataSlice.actions;
export const spacesUsersData = spacesUsersDataSlice.reducer;

// Slice to store project entities list.
// `unknown[]`, not an element type: nothing in the app imports `setEntityListAction` and nothing
// reads `RootState["setEntityList"]`, so no dispatcher exists to take the element shape from.
const initialEntityListState: unknown[] = [];
const entityListSlice = createSlice({
  name: "entityList",
  initialState: initialEntityListState,
  reducers: {
    setEntityList: (_state, action: PayloadAction<unknown[]>) => action.payload,
  },
});
export const { setEntityList: setEntityListAction } = entityListSlice.actions;
export const setEntityList = entityListSlice.reducer;

// Slice for deleted space status
// Dispatched by `deleteSpace` (actions/projectSetting.ts) with the `/spaces/removeSpace` SUCCESS `result`,
// an OBJECT the route always sends. The slice is seeded with an empty array, hence the union — `[]` is
// not that object.
type RemovedSpaceResult = ApiSuccess<"/spaces/removeSpace">["result"];
type DeletedSpaceState = RemovedSpaceResult | never[];
// Seeded through `createSlice`'s lazy-initialiser overload, which is the only form that keeps the
// union: given a UNION-typed `initialState` VALUE, `State` inference collapses to its last
// constituent (`never[]`), and the reducer below — which returns the object arm — then fails to
// typecheck against `CaseReducer<never[], …>`. The initial value is `[]`.
const deleteSpaceSlice = createSlice({
  name: "deleteSpace",
  initialState: (): DeletedSpaceState => [],
  reducers: {
    deletedSpaceStatus: (_state, action: PayloadAction<RemovedSpaceResult>) => action.payload,
  },
});
export const { deletedSpaceStatus } = deleteSpaceSlice.actions;
export const deleteSpace = deleteSpaceSlice.reducer;

// Slice for deleted user status.
// `unknown[]` for the same reason as `entityList`: nothing imports `deletedUserStatus` and nothing
// reads `RootState["DeletedUserState"]`, so there is no dispatcher to take the element shape from.
const initialDeletedUserState: unknown[] = [];
const deletedUserSlice = createSlice({
  name: "DeletedUserState",
  initialState: initialDeletedUserState,
  reducers: {
    deletedUserStatus: (_state, action: PayloadAction<unknown[]>) => action.payload,
  },
});
export const { deletedUserStatus } = deletedUserSlice.actions;
export const DeletedUserState = deletedUserSlice.reducer;

// Slice for project setting object: the per-user capability flags of one Project, dispatched by
// `getProjectObject` (actions/projectSetting.ts) with the `GET /projects/getProjectObject/:id` `result`.
type ProjectObjectResult = ApiSuccessOf<"get", "/projects/getProjectObject/:id">["result"];
const initialProjectSettingObjectState: NonNullable<ProjectObjectResult> = {};
const projectSettingObjectSlice = createSlice({
  name: "getProjectSettingObject",
  initialState: initialProjectSettingObjectState,
  reducers: {
    getProjectSettingObject: (_state, action: PayloadAction<ProjectObjectResult>) => action.payload ?? {},
  },
});

// `useStickySidebar` is the only dispatcher and sends `StickySidebarIcon` narrowed to four fields.
const initialSidebarIconNameState: SidebarIconNameEntry[] = [];
const projectSideBarIconAndNameMapper = createSlice({
  name: "projectSideBarIconsAndNameMapper",
  initialState: initialSidebarIconNameState,
  reducers: {
    setProjectSideBarIconsAndNameMapper: (_state, action: PayloadAction<SidebarIconNameEntry[]>) => action.payload,
  },
});
export const { setProjectSideBarIconsAndNameMapper } = projectSideBarIconAndNameMapper.actions;
export const projectSideBarIconAndNameMapperReducer = projectSideBarIconAndNameMapper.reducer;
export const { getProjectSettingObject: getProjectSettingObjectAction } = projectSettingObjectSlice.actions;
export const getProjectSettingObject = projectSettingObjectSlice.reducer;
